// Reward machines (B16). Three ideas, kept apart:
//  - a *card set* (src/domain/arcade/cardSets, the CardSet type) is what a play asks;
//  - a *reward machine* (this folder) is the cabinet a child spends a play on, and how the reward
//    opens: Japan's gashapon, a neutral chest, later a piñata or a claw machine;
//  - *the economy* (plays.ts, drawPrize, prizes, Needs you) is the same for every machine.
// A machine only changes the look, the words, the sounds and the buzz. Which one a trip uses is
// rewardMachineFor(pack) (src/domain/rewardMachine.ts): the parent's choice, the kit's, or the
// neutral one.
import type { ComponentType } from 'react'
import type * as THREE from 'three'
import type { RewardMachineId } from '../../../shared/rewards'
import type { CardSetTheme } from '../../domain/arcade/types'
import type { PoolPrize } from '../../domain/rewards'
import type { Haptic } from '../../audio/haptics'
import type { MotionTier } from '../../motion/tier'
import type { Kit } from '../../map/world/kit'
import type { Puffs } from '../../map/world/particles'
import type { Rng } from '../../domain/rng'

/** Every child-facing word about the machine (shared/machineWords.ts; en.ts reads these, never "capsule"). */
export type { MachineWords } from '../../../shared/machineWords'
import type { MachineWords } from '../../../shared/machineWords'

export interface FlatMachineProps {
  /** The card set's name, on the cabinet's plate. */
  title: string
  theme: CardSetTheme
  locked: boolean
  tier: MotionTier
  /** Main button text (the machine's own `words.action`). */
  label: string
  /** The spend-a-play moment is over: the twist is paid and the reveal opens (WI-13). */
  onDropped(): void
}

/**
 * WI-13: what the machine opens onto, decided before the reveal starts (domain/release
 * `drawPayout`). Exactly one thing: a collectible charm, an act key, or a real prize's ticket.
 * Machines draw it with src/arcade/reveal3d.ts `revealContent` (3D) or `look.emoji` (flat), so a
 * new machine only has to open, not to know the economy.
 */
export interface RevealLook {
  kind: 'collectible' | 'key' | 'prize'
  /** The thing's picture: the collectible's or prize's emoji, the act key's island stand-in. */
  emoji: string
  /** Generated sticker art (collectibles), when the kit has it. */
  art?: string
  /** The opening's colour: rarity for a prize, the set's for a collectible, purple for a key. */
  colour: string
  /** A collectible's third copy: it fuses gold. */
  gold?: boolean
}

/**
 * The reveal hook (WI-13; Lane 2's claw machine plugs in here). Full screen, behind the reveal's
 * words and buttons (Reward.tsx draws those): the machine's own opening, ending on `look` shown big
 * in the middle of its box. About 3 s at most, skippable (a tap closes the reveal); `still`: the end
 * frame at once; `calm`: no WebGL (draw it flat). Calls nothing back: the payout is already recorded.
 */
export interface RevealProps {
  look: RevealLook
  still: boolean
  calm: boolean
}

export interface RewardMachine {
  id: RewardMachineId
  /** A stand-in look, waiting for the owner's pick (renders first). */
  placeholder?: boolean
  words: MachineWords
  /** The 2D cabinet: Calm/Still, no WebGL, or while the 3D stage loads. */
  Flat: ComponentType<FlatMachineProps>
  /** The reveal: the machine opens straight onto one reward (no card round, WI-13). */
  Reveal: ComponentType<RevealProps>
  /** Sounds: picked natural takes (src/audio/appSounds.ts) with synth cues (src/audio/synth.ts) as the fallback. */
  sounds: { spend?(): void; open?(): void; reveal(kind: RevealLook['kind'], rarity?: PoolPrize['rarity']): void }
  /** Haptic names (src/audio/haptics.ts) for the spend and the open. */
  haptics?: { spend: Haptic; open: Haptic }
}

// ---------- the 3D cabinet on the Arcade set ----------
// Built in the stage chunk only (cabinets3d.ts), so three.js never lands in the main bundle.

export interface CabinetLook {
  title: string
  colour: CardSetTheme['color']
  locked: boolean
}

/** What the Arcade set lends a cabinet: its resources, the camera, the puffs and a redraw. */
export interface CabinetRig {
  kit: Kit
  env: THREE.Texture
  camera: THREE.PerspectiveCamera
  /** The camera's resting view of the cabinet. */
  home: { pos: THREE.Vector3; target: THREE.Vector3 }
  /** The camera's direction from its target (for push-ins). */
  dir: THREE.Vector3
  puffs: Puffs
  rand: Rng
  kick(): void
}

export interface Cabinet3D {
  readonly group: THREE.Group
  /** Pieces that move apart from the cabinet (a capsule rolling out); the set adds them beside it. */
  readonly loose: THREE.Object3D[]
  /** Height range to frame on screen (world units, from the base). */
  readonly box: { yMin: number; yMax: number }
  readonly locked: boolean
  /** Seconds from the spend to the open (the set also finishes on a timer). */
  readonly duration: number
  setLook(look: CabinetLook): void
  /** The spend-a-play sequence starts (the set has checked it's idle and unlocked). */
  begin(): void
  /** One frame of the sequence, `t` seconds in. */
  step(t: number, dt: number): void
  /** Back to idle (also behind the round of cards, once it covers the screen). */
  reset(): void
  /** Idle motion; true while anything moved. */
  idle(dt: number, live: boolean): boolean
  /** Pointer on the cabinet: true if it took the gesture (e.g. a dial). */
  pointerDown(ndc: THREE.Vector2, ray: THREE.Raycaster): boolean
  pointerMove(ndc: THREE.Vector2): void
  /** The gesture ended: true starts the spend. */
  pointerUp(): boolean
  /** A tap: 'spend' starts it; anything else is the cabinet's own fun. */
  tap(ray: THREE.Raycaster): 'spend' | null
}

export type CabinetBuilder = (rig: CabinetRig) => Cabinet3D

/**
 * A 2D cabinet's spend animation, capped: the round opens on time even if the animation never
 * finishes (a paused tab, a stalled compositor), as the 3D set's timer does.
 */
export function capped(run: () => Promise<unknown>, ms: number): Promise<void> {
  return Promise.race([run().then(() => undefined, () => undefined), new Promise<void>((r) => setTimeout(r, ms))])
}
